import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import {
  MdSettings as SettingsIcon,
  MdExpandMore as ExpandMoreIcon,
  MdFileDownload as FileDownloadIcon,
} from 'react-icons/md';
import { getTransactionLogs, getTransactionTypes, TransactionLog } from '../services/api';
import { useSidebar } from '../contexts/SidebarContext';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';

interface ColumnConfig {
  key: keyof TransactionLog;
  label: string;
  width?: number;
  sortable?: boolean;
  visible: boolean;
}

/** Map semantic tone names (shared with classic MUI Chip colors) to new-GUI token chip classes. */
const toneChipClass = (tone: string): string => {
  switch (tone) {
    case 'success':
      return 'bg-success/10 text-success border-success/30';
    case 'error':
      return 'bg-danger/10 text-danger border-danger/30';
    case 'warning':
      return 'bg-warning/10 text-warning border-warning/30';
    case 'info':
      return 'bg-info/10 text-info border-info/30';
    default:
      return 'bg-accent/10 text-accent border-accent/30';
  }
};

const AuditPage: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const [logs, setLogs] = useState<TransactionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);

  // Filters
  const [transactionType, setTransactionType] = useState<string>('');
  const [requestingUserId, setRequestingUserId] = useState<string>('');
  const [toolName, setToolName] = useState<string>('');
  const [limit, setLimit] = useState<number>(50);

  // Column configuration
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'id', label: 'ID', width: 80, visible: true },
    { key: 'sid', label: 'System ID', width: 120, visible: false },
    { key: 'TransactionId', label: 'Transaction UUID', width: 200, visible: false },
    { key: 'infraMachine', label: 'Backend Server', width: 140, visible: false },
    { key: 'transactionType', label: 'Type', width: 120, visible: true },
    { key: 'requestingUserId', label: 'Requesting User', width: 140, visible: true },
    { key: 'transactionStatus', label: 'Status', width: 100, visible: true },
    { key: 'refTable', label: 'Reference Table', width: 130, visible: false },
    { key: 'records', label: 'Affected Records', width: 140, visible: false },
    { key: 'toolName', label: 'Tool', width: 120, visible: true },
    { key: 'licenseCount', label: 'Licenses', width: 100, visible: true },
    { key: 'serverInfo', label: 'Server Info', width: 150, visible: true },
    { key: 'licSrvrConnectStr', label: 'License Server', width: 180, visible: true },
    { key: 'machineIP', label: 'Client IP', width: 130, visible: false },
    { key: 'TransactionRequestTime', label: 'Timestamp', width: 160, visible: true },
    { key: 'remarks', label: 'Remarks', width: 200, visible: true },
    { key: 'responseText', label: 'Response Data', width: 200, visible: false },
  ]);

  // Pre-populated known transaction types
  const knownTransactionTypes = [
    'LICENSE_ADD', 'LICENSE_REMOVE', 'LICENSE_UPDATE',
    'LICENSE_SYNC', 'LICENSE_REREAD',
    'refreshInfraUsers', 'ExtendAccountExpiry',
    'restartVncForInfraUser', 'generateInfraUsersStd',
    'disableInfraUser', 'enableInfraUser',
    'UserGeneration', 'UserDisable'
  ];

  // Load column preferences from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('auditPageColumnConfig');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setColumns(prevColumns =>
          prevColumns.map(col => ({
            ...col,
            visible: parsedConfig[col.key] !== undefined ? parsedConfig[col.key] : col.visible
          }))
        );
      } catch (error) {
        console.warn('Failed to load column configuration from localStorage:', error);
      }
    }
  }, []);

  // Save column preferences to localStorage
  const saveColumnConfig = (newColumns: ColumnConfig[]) => {
    const config = newColumns.reduce((acc, col) => {
      acc[col.key] = col.visible;
      return acc;
    }, {} as Record<string, boolean>);
    localStorage.setItem('auditPageColumnConfig', JSON.stringify(config));
  };

  const loadTransactionLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = { limit };
      if (transactionType) params.transactionType = transactionType;
      if (requestingUserId) params.requestingUserId = requestingUserId;
      if (toolName) params.toolName = toolName;

      const data = await getTransactionLogs(params);
      setLogs(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load transaction logs');
    } finally {
      setLoading(false);
    }
  };

  const handleColumnToggle = (key: keyof TransactionLog) => {
    setColumns(prevColumns => {
      const newColumns = prevColumns.map(col =>
        col.key === key ? { ...col, visible: !col.visible } : col
      );
      saveColumnConfig(newColumns);
      return newColumns;
    });
  };

  const handleExportCSV = () => {
    if (logs.length === 0) return;

    const visibleColumns = columns.filter(col => col.visible);
    const headers = visibleColumns.map(col => col.label);
    const csvData = logs.map(log =>
      visibleColumns.map(col => {
        const value = log[col.key];
        if (col.key === 'TransactionRequestTime') {
          return new Date(value as string).toLocaleString();
        }
        return value || '';
      })
    );

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type.toUpperCase()) {
      // License Operations (Green/Blue theme)
      case 'LICENSE_ADD': return 'success';        // 🟢 ADD-license
      case 'LICENSE_REMOVE': return 'error';       // 🔴 REMOVE
      case 'LICENSE_UPDATE': return 'warning';     // 🟠 UPDATE
      case 'LICENSE_SYNC': return 'info';          // 🔵 SYNC
      case 'LICENSE_REREAD': return 'secondary';   // ⚫ REREAD

      // User Operations (Purple theme)
      case 'ENABLEINFRAUSER': return 'primary';    // 🟣 ENABLE
      case 'DISABLEINFRAUSER': return 'warning';   // 🟠 DISABLE
      case 'USER_GENERATION': return 'success';    // 🟢 USER_GENERATION
      case 'USERGENERATION': return 'success';     // 🟢 (alt format)

      // Extension Operations (Orange theme)
      case 'EXTENSION_REQUEST': return 'warning';  // 🟠 EXTENSION_REQUEST
      case 'EXTENDACCOUNTEXPIRY': return 'warning'; // 🟠 (alt format)
      case 'EXTENSION_APPROVED': return 'success'; // 🟢 EXTENSION_APPROVED

      // System Operations (Teal/Cyan theme)
      case 'REFRESHINFRAUSERS': return 'info';     // 🔵 REFRESHINFRAUSERS
      case 'RESTARTVNCFORINFRAUSER': return 'secondary'; // ⚫ RESTART_VNC

      default: return 'default'; // 🔵 Default blue
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'inprogress': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const formatCellValue = (column: ColumnConfig, value: any) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (column.key) {
      case 'TransactionRequestTime':
        return new Date(value).toLocaleString();
      case 'responseText':
        if (useNew) {
          try {
            const parsed = JSON.parse(value);
            return (
              <details className="text-xs">
                <summary className="cursor-pointer text-accent hover:text-accent-hover">JSON Data</summary>
                <pre className="mt-1 font-plexMono text-ink-secondary whitespace-pre-wrap">
                  {JSON.stringify(parsed, null, 2)}
                </pre>
              </details>
            );
          } catch {
            return <span className="font-plexMono text-xs text-ink-primary">{value}</span>;
          }
        }
        try {
          const parsed = JSON.parse(value);
          return (
            <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">JSON Data</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(parsed, null, 2)}
                </pre>
              </AccordionDetails>
            </Accordion>
          );
        } catch {
          return <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{value}</Typography>;
        }
      case 'transactionType': {
        const label = value.replace('LICENSE_', '');
        const tone = getTransactionTypeColor(value);
        if (useNew) {
          return (
            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${toneChipClass(tone)}`}>
              {label}
            </span>
          );
        }
        return (
          <Chip
            label={label}
            color={tone as 'success' | 'error' | 'warning' | 'info' | 'secondary' | 'primary' | 'default'}
            size="small"
          />
        );
      }
      case 'transactionStatus': {
        const tone = getStatusColor(value);
        if (useNew) {
          return (
            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${toneChipClass(tone)}`}>
              {value}
            </span>
          );
        }
        return (
          <Chip
            label={value}
            color={tone as 'success' | 'error' | 'warning' | 'info' | 'default'}
            size="small"
            variant="outlined"
          />
        );
      }
      case 'licenseCount':
        if (useNew) {
          return <span className="block text-right text-ink-primary">{value}</span>;
        }
        return <Typography align="right">{value}</Typography>;
      default:
        if (typeof value === 'string' && value.length > 50) {
          if (useNew) {
            return (
              <span
                title={value}
                className="block text-ink-primary overflow-hidden text-ellipsis whitespace-nowrap"
                style={{ maxWidth: column.width || 150 }}
              >
                {value}
              </span>
            );
          }
          return (
            <Tooltip title={value}>
              <Typography
                sx={{
                  maxWidth: column.width || 150,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {value}
              </Typography>
            </Tooltip>
          );
        }
        return value;
    }
  };

  // Load available transaction types on component mount
  useEffect(() => {
    const loadTransactionTypes = async () => {
      try {
        const dynamicTypes = await getTransactionTypes();
        const allTypes = Array.from(new Set([...knownTransactionTypes, ...dynamicTypes]));
        setAvailableTypes(allTypes.sort());
      } catch (error) {
        console.warn('Failed to load dynamic transaction types, using known types only:', error);
        setAvailableTypes(knownTransactionTypes);
      }
    };

    loadTransactionTypes();
    loadTransactionLogs();
  }, []);

  const visibleColumns = columns.filter(col => col.visible);

  const fieldClass = useNew
    ? 'border border-border-strong rounded px-3 py-2 text-sm bg-surface text-ink-primary focus:outline-none focus:border-accent'
    : '';

  const columnConfigDialog = (
    <Dialog
      open={columnConfigOpen}
      onClose={() => setColumnConfigOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Configure Visible Columns</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select which columns to display in the audit logs table:
        </Typography>
        <Grid container spacing={1}>
          {columns.map((column) => (
            <Grid item xs={6} key={column.key}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={column.visible}
                    onChange={() => handleColumnToggle(column.key)}
                  />
                }
                label={column.label}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setColumnConfigOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  if (useNew) {
    return (
      <div className={`font-plex transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <div className="max-w-screen-xl mx-auto px-6 pt-6 pb-8">
          <PageHeader
            title="Audit Logs"
            purpose="Complete audit trail of all license operations for compliance and monitoring."
            actions={
              <button
                type="button"
                onClick={() => setColumnConfigOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-surface text-accent border border-accent font-medium rounded-lg hover:bg-accent/5 transition-all duration-200 text-sm"
              >
                <SettingsIcon className="text-base" />
                Configure Columns
              </button>
            }
          />

          {/* Filters — token chrome (mirrors Server Accounts toolbar container) */}
          <div className="bg-surface-raised border-2 border-border-strong rounded p-4 mb-4">
            <h3 className="text-sm font-semibold text-ink-primary mb-3">Filters</h3>
            <div className="flex gap-2 flex-wrap items-center">
              <select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                className={`${fieldClass} min-w-[150px]`}
                aria-label="Transaction Type"
              >
                <option value="">All Types</option>
                {availableTypes.map((type: string) => (
                  <option key={type} value={type}>{type.replace('LICENSE_', '')}</option>
                ))}
              </select>

              <input
                type="text"
                placeholder="Requesting User ID"
                value={requestingUserId}
                onChange={(e) => setRequestingUserId(e.target.value)}
                className={`${fieldClass} min-w-[120px]`}
              />

              <input
                type="text"
                placeholder="Tool Name"
                value={toolName}
                onChange={(e) => setToolName(e.target.value)}
                className={`${fieldClass} min-w-[120px]`}
              />

              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className={`${fieldClass} min-w-[100px]`}
                aria-label="Limit"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
              </select>

              <button
                type="button"
                onClick={loadTransactionLogs}
                className="px-4 py-2 bg-accent text-white font-medium rounded-lg shadow-sm hover:bg-accent-hover transition-all duration-200 text-sm"
              >
                Apply Filters
              </button>

              <button
                type="button"
                onClick={handleExportCSV}
                disabled={logs.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-surface text-accent border border-accent font-medium rounded-lg hover:bg-accent/5 transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileDownloadIcon className="text-base" />
                Export CSV
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-danger/10 text-danger border border-danger/30 text-sm">
              {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-accent" />
              <span className="ml-3 text-ink-secondary">Loading transaction logs...</span>
            </div>
          )}

          {!loading && !error && (
            <div className="mb-3 px-3 py-2 rounded border border-border-subtle bg-surface text-sm text-ink-secondary">
              Showing {logs.length} transaction logs
              {logs.length > 0 && ` (Last updated: ${new Date().toLocaleString()})`}
            </div>
          )}

          {!loading && !error && logs.length > 0 && (
            <div className="overflow-auto border-2 border-border-strong rounded bg-surface">
              <table
                className="w-full text-sm"
                style={{ minWidth: visibleColumns.reduce((sum, col) => sum + (col.width || 150), 0) }}
              >
                <thead className="bg-surface-raised">
                  <tr>
                    {visibleColumns.map((column) => (
                      <th
                        key={column.key}
                        className="py-2 px-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-secondary border-b border-border-subtle"
                        style={{ minWidth: column.width }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-surface-raised border-b border-border-subtle">
                      {visibleColumns.map((column) => (
                        <td key={column.key} className="py-2 px-3 text-ink-primary align-top">
                          {formatCellValue(column, log[column.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && logs.length === 0 && (
            <div className="border-2 border-border-strong rounded bg-surface p-8 text-center">
              <p className="text-base font-medium text-ink-secondary">No transaction logs found</p>
              <p className="text-sm text-ink-secondary mt-1">
                Try adjusting your filters or check back later for new audit entries.
              </p>
            </div>
          )}

          {columnConfigDialog}
        </div>
      </div>
    );
  }

  return (
    <div className={`transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Transaction Audit Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete audit trail of all license operations for compliance and monitoring
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setColumnConfigOpen(true)}
        >
          Configure Columns
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Transaction Type</InputLabel>
            <Select
              value={transactionType}
              label="Transaction Type"
              onChange={(e) => setTransactionType(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              {availableTypes.map((type: string) => (
                <MenuItem key={type} value={type}>{type.replace('LICENSE_', '')}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Requesting User ID"
            value={requestingUserId}
            onChange={(e) => setRequestingUserId(e.target.value)}
            sx={{ minWidth: 120 }}
          />

          <TextField
            size="small"
            label="Tool Name"
            value={toolName}
            onChange={(e) => setToolName(e.target.value)}
            sx={{ minWidth: 120 }}
          />

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Limit</InputLabel>
            <Select
              value={limit}
              label="Limit"
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={500}>500</MenuItem>
            </Select>
          </FormControl>

          <Button variant="contained" onClick={loadTransactionLogs}>
            Apply Filters
          </Button>

          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCSV}
            disabled={logs.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Results Summary */}
      {!loading && !error && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2">
            Showing {logs.length} transaction logs
            {logs.length > 0 && ` (Last updated: ${new Date().toLocaleString()})`}
          </Typography>
        </Paper>
      )}

      {/* Transaction Logs Table */}
      {!loading && !error && logs.length > 0 && (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflow: 'auto' }}>
          <Table sx={{ minWidth: visibleColumns.reduce((sum, col) => sum + (col.width || 150), 0) }}>
            <TableHead>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    sx={{
                      minWidth: column.width,
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50'
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  {visibleColumns.map((column) => (
                    <TableCell key={column.key}>
                      {formatCellValue(column, log[column.key])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* No Data */}
      {!loading && !error && logs.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No transaction logs found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or check back later for new audit entries.
          </Typography>
        </Paper>
      )}

      {columnConfigDialog}
      </Container>
    </div>
  );
};

export default AuditPage;
